import { useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Chip, Popover } from '@c2fo/liquidity';
import { ArrowRightIcon } from '@c2fo/liquidity/icons';
import invoices from '@/enterprise/assets/invoicesgrey.svg?url';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  TableCell,
  TableDisplay,
  TableSortArrow,
} from '@/enterprise/components/Table';
import usePlayableInvoices, { PlayableInvoiceSearchFilters } from '@/enterprise/data/usePlayableInvoices';
import { TakerMarket } from '@/enterprise/data/useTakerMarkets';
import InvoiceAdjustmentPopover from '@/enterprise/features/eligibleInvoiceTable/components/InvoiceAdjustmentPopover';
import useLocaleFormat from '@/utils/useLocaleFormat';
import useUserHasIndiaDivisions from '@/utils/useUserHasIndiaDivisions';
import EmptyRender from '../../EmptyRender';
import NoDataState from '../../NoDataState';
import { useInvoiceCentralLayoutContext } from '../InvoiceCentralLayout';
import { EligibleInvoiceUrlState } from './EligibleInvoices';
import { mapPlayableInvoicesToEligibleInvoices, statusChipConfig } from './utils';

const mapUrlStateToApiSearchFilters = ({
  urlState,
}: {
  urlState: EligibleInvoiceUrlState;
}): PlayableInvoiceSearchFilters => {
  const search: PlayableInvoiceSearchFilters = {
    // Even though searchValue is typed as string, useUrlState is parsing it as a number when it's just digits
    // e.g. "123" becomes 123, so cast it to string for API
    ...(urlState.searchValue && { voucherPatterns: [String(urlState.searchValue)] }),
    ...((!!urlState.filters?.amount?.from || !!urlState.filters?.amount?.to) && {
      amount: [
        {
          ...(!!urlState.filters.amount?.from && { gte: urlState.filters.amount.from }),
          ...(!!urlState.filters.amount?.to && { lte: urlState.filters.amount.to }),
        },
      ],
    }),
    ...((!!urlState.filters?.originalDueDate?.from || !!urlState.filters?.originalDueDate?.to) && {
      dueDate: [
        {
          ...(!!urlState.filters.originalDueDate?.from && { from: urlState.filters.originalDueDate.from }),
          ...(!!urlState.filters.originalDueDate?.to && { to: urlState.filters.originalDueDate.to }),
        },
      ],
    }),
  };

  return Object.keys(search).length > 0 ? search : undefined;
};

export const mapUrlStateToIncludedTakerMarkets = ({
  urlState,
  takerMarkets,
}: {
  urlState: EligibleInvoiceUrlState;
  takerMarkets: TakerMarket[];
}) => {
  let includedTakerMarkets: TakerMarket[] = takerMarkets;
  const { paymentSent, paymentDeposit, takerMakerId } = urlState.filters ?? {};

  if (takerMakerId) {
    // useUrlState decodes digit-only strings as numbers (e.g. "332913977" → 332913977),
    // so normalize to string before comparing against tm.takerMakerId.
    const normalizedFilter = String(takerMakerId);
    includedTakerMarkets = includedTakerMarkets.filter((tm) => tm.takerMakerId === normalizedFilter);
  }

  if (paymentSent) {
    const { from, to } = paymentSent;

    // Filter takerMarkets to only include those with next pay date after from date
    includedTakerMarkets = from
      ? includedTakerMarkets.filter((tm) => tm.marketPayDate && tm.marketPayDate >= from)
      : includedTakerMarkets;

    // Filter takerMarkets to only include those with next pay date before to date
    includedTakerMarkets = to
      ? includedTakerMarkets.filter((tm) => tm.marketPayDate && tm.marketPayDate <= to)
      : includedTakerMarkets;
  }

  if (paymentDeposit) {
    const { from, to } = paymentDeposit;

    // Filter takerMarkets to only include those with next good funds date after from date
    includedTakerMarkets = from
      ? includedTakerMarkets.filter((tm) => tm.marketGoodFundsDate && tm.marketGoodFundsDate >= from)
      : includedTakerMarkets;

    // Filter takerMarkets to only include those with next good funds date before to date
    includedTakerMarkets = to
      ? includedTakerMarkets.filter((tm) => tm.marketGoodFundsDate && tm.marketGoodFundsDate <= to)
      : includedTakerMarkets;
  }

  return includedTakerMarkets;
};

export const EligibleInvoicesTable = ({
  currentCursor,
  setNextCursor,
  urlState,
  handleSort,
}: {
  currentCursor: string | null;
  setNextCursor: (cursor: string | null) => void;
  urlState: EligibleInvoiceUrlState;
  handleSort: (sortKey: EligibleInvoiceUrlState['sort']['key']) => void;
}) => {
  const { t } = useTranslation();
  const { asNumber, asDateStringText, asCurrency } = useLocaleFormat();
  const {
    buyer: { takerMarkets },
    urlParams: { makerOrganizationUuid },
  } = useInvoiceCentralLayoutContext();

  const hasIndiaDivisions = useUserHasIndiaDivisions();

  const { sort } = urlState;

  const search = useMemo(() => mapUrlStateToApiSearchFilters({ urlState }), [urlState]);
  const includedTakerMarkets = useMemo(
    () => mapUrlStateToIncludedTakerMarkets({ urlState, takerMarkets }),
    [urlState, takerMarkets]
  );

  const { data } = usePlayableInvoices({
    makerOrganizationUuid: makerOrganizationUuid,
    takerMarkets: includedTakerMarkets,
    options: {
      sortBy: sort.key,
      sortDirection: sort.direction === 'asc' ? 'ASC' : 'DESC',
      ...(currentCursor && { cursor: currentCursor }),
      limit: urlState.pageSize,
    },
    ...(!!search && {
      search,
    }),
  });

  const nextCursor = data?.pageInfo?.nextCursor ?? null;

  useEffect(() => {
    setNextCursor(nextCursor);
  }, [nextCursor, setNextCursor]);

  const eligibleInvoices = useMemo(
    () => mapPlayableInvoicesToEligibleInvoices({ invoices: data?.invoices ?? [], takerMarkets }),
    [data?.invoices, takerMarkets]
  );

  return (
    <div className="overflow-auto">
      <Table>
        <TableHeader>
          <TableRow>
            <TableHead isSorted={sort.key === 'voucherId'} onClick={() => handleSort('voucherId')}>
              <TableSortArrow accessorKey="voucherId" sort={sort}>
                {t('core.invoiceId')}
              </TableSortArrow>
            </TableHead>
            <TableHead isSorted={sort.key === 'amount'} onClick={() => handleSort('amount')}>
              <TableSortArrow accessorKey="amount" sort={sort} textRight>
                {t('core.invoiceAmount')}
              </TableSortArrow>
            </TableHead>
            <TableHead isSorted={sort.key === 'originalDepositDate'} onClick={() => handleSort('originalDepositDate')}>
              <TableSortArrow accessorKey="originalDepositDate" sort={sort}>
                {hasIndiaDivisions ? t('core.tentativePaymentDate') : t('core.originalDueDate')}
              </TableSortArrow>
            </TableHead>
            {/* No API sorting for payDate */}
            <TableHead>{t('invoiceCentral.earlyPaymentSent')}</TableHead>
            <TableHead
              isSorted={sort.key === 'daysPaidEarly'}
              onClick={() => handleSort('daysPaidEarly')}
              className="text-right"
            >
              <TableSortArrow accessorKey="daysPaidEarly" sort={sort} textRight>
                {t('core.daysPaidEarly')}
              </TableSortArrow>
            </TableHead>
            {/* No API sorting for goodFundsDate */}
            <TableHead>{t('draftOffer.labels.estimatedDepositDate')}</TableHead>
            {/* Partial API sorting, can sort include/exclude but not takerConfig.isEnabled if not excluded */}
            <TableHead className="text-right">{t('core.status')}</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {eligibleInvoices.length > 0 ? (
            eligibleInvoices.map((eligibleInvoice) => {
              return (
                <TableRow key={eligibleInvoice.voucherId}>
                  {/* Invoice Id */}
                  <TableCell>
                    <div className="max-w-80 whitespace-pre-wrap break-words">{eligibleInvoice.voucherId}</div>
                  </TableCell>
                  {/* Invoice Amount */}
                  <TableCell className="text-right">
                    {!!eligibleInvoice.adjustmentAmount ? (
                      <InvoiceAdjustmentPopover
                        adjustmentAmount={eligibleInvoice.adjustmentAmount}
                        amount={eligibleInvoice.amount}
                        currency={eligibleInvoice.currency}
                        originalAmount={eligibleInvoice.originalAmount}
                        coversUnmatched={eligibleInvoice.coversUnmatched}
                      />
                    ) : (
                      asCurrency(eligibleInvoice.amount, eligibleInvoice.currency)
                    )}
                  </TableCell>
                  {/* Original Due Date */}
                  <TableCell>
                    {eligibleInvoice.dueDate && asDateStringText({ date: eligibleInvoice.dueDate })}
                  </TableCell>
                  {/* Early Payment Sent */}
                  <TableCell>
                    <EmptyRender condition={!!eligibleInvoice.payDate}>
                      <div className="flex items-center gap-4 2xl:gap-6">
                        <ArrowRightIcon className="-ml-8 2xl:-ml-10" />
                        {eligibleInvoice.payDate && asDateStringText({ date: eligibleInvoice.payDate })}
                      </div>
                    </EmptyRender>
                  </TableCell>
                  {/* Days Paid Early */}
                  <TableCell className="text-right">
                    <EmptyRender condition={!!eligibleInvoice.daysPaidEarly && eligibleInvoice.daysPaidEarly > 0}>
                      {t('core.daysWithCount', {
                        count: eligibleInvoice.daysPaidEarly,
                        formattedCount: asNumber({ value: eligibleInvoice.daysPaidEarly }),
                      })}
                    </EmptyRender>
                  </TableCell>
                  {/* Estimated Deposit Date */}
                  <TableCell>
                    <EmptyRender condition={!!eligibleInvoice.goodFundsDate}>
                      {eligibleInvoice.goodFundsDate && asDateStringText({ date: eligibleInvoice.goodFundsDate })}
                    </EmptyRender>
                  </TableCell>
                  {/* Status */}
                  <TableCell className="flex justify-end">
                    <Popover>
                      <Popover.Trigger aria-label="Status information">
                        <Chip
                          label={t(statusChipConfig[eligibleInvoice.status].labelKey)}
                          type={statusChipConfig[eligibleInvoice.status].type}
                          useHoverStyles
                        />
                      </Popover.Trigger>
                      <Popover.Content arrow collisionPadding={24}>
                        <div className="w-72 space-y-2 text-center text-sm">
                          <div>{t(statusChipConfig[eligibleInvoice.status].tooltipKey)}</div>
                        </div>
                      </Popover.Content>
                    </Popover>
                  </TableCell>
                </TableRow>
              );
            })
          ) : (
            <TableDisplay colSpan={7}>
              <NoDataState
                className="py-8"
                icon={<img src={invoices} alt="emptystatements" className="h-28 w-28" />}
                title={t('invoices.noAvailableInvoices')}
                message={t('invoices.noAvailableEligibleInvoicesDescription')}
              />
            </TableDisplay>
          )}
        </TableBody>
      </Table>
    </div>
  );
};
